import React, { SyntheticEvent, useState, useRef, useEffect, ChangeEvent } from "react";

import { HexAlphaColorPicker, HexColorPicker } from "react-colorful";
import { AnyColor } from "react-colorful/dist/types";
import { colord, extend, getFormat } from "colord";
import namesPlugin from "colord/plugins/names";

import { Popover, PopoverOrigin, TextField, useTheme, darken, lighten,
          Stack, ButtonBase, IconButton, Avatar,  Box, Typography, Alert} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DoNotDisturb, DeleteForever, SquareRounded } from "@mui/icons-material";

import i18n from 'translation';
import { useDebounce } from "hooks/debounce";

export type { AnyColor }

extend([namesPlugin]);

interface PopoverColorPickerProps {
  anchor: HTMLElement;
  anchorOrigin?: PopoverOrigin;
  color: AnyColor | undefined;
  defaultColor?: AnyColor;
  disableAlpha?: boolean;
  isOpen: boolean;
  onChange: (color: AnyColor) => void;
  onClose: () => void;
  showInput?: boolean;
  transformOrigin?: PopoverOrigin;
}

export default function PopoverColorPicker(props: PopoverColorPickerProps): React.JSX.Element {
  const theme = useTheme();
  const popRef = useRef<HTMLDivElement | null>(null);
  const allowCloseOnClick = useRef(false);

  const [anchorOrigin, /* setAnchorOrigin */] = useState<PopoverOrigin>(props.anchorOrigin || {
    vertical: "bottom",
    horizontal: "left"
  });
  const [transformOrigin, /* setTransformOrigin */] = useState<PopoverOrigin>(props.transformOrigin || {
    vertical: "top",
    horizontal: "left"
  });

  const [pickerColor, setPickerColor] = useState<AnyColor>(props.color || props.defaultColor || '');
  const [inputColor, setInputColor] = useState<AnyColor>(props.defaultColor || "#000000");
  const [didRemoveColor, setDidRemoveColor] = useState<boolean>(false);

  const [helpText, setHelpText] = useState("");
  const [debounceHelpText, setDebounceHelpText] = useState("");

  useEffect(() => {
    if (colord(props.color ?? '').isValid()) {
      setPickerColor(props.color ?? '');
      setInputColor(props.color ?? '');
    }
    else if (props.defaultColor) {
      setPickerColor(props.defaultColor);
      setInputColor(props.defaultColor);
    }
  }, [props.color]);

  const debounceHelpTextChanged = useDebounce(() => {
    if (debounceHelpText) {
      setHelpText(debounceHelpText);
    }
  }, 300);

  const handleInputColorChange = (color: AnyColor) => {
    setPickerColor(color);
    setInputColor(color);
    props.onChange(color);
  };

  const handleInputChange = (value: string) => {
    const newColor = value;
    const colorFormat = getFormat(newColor);

    if (colorFormat) {
      // valid color
      setDebounceHelpText("");
      setHelpText("");
      setPickerColor(newColor);
      setInputColor(newColor);
      props.onChange(newColor);
    } else {
      // invalid color
      setDebounceHelpText(i18n.t("color-selector.validation.invalid-color-format"));
      debounceHelpTextChanged();
      setInputColor(newColor);
    }
  };

  const handleMouseDown = (event: SyntheticEvent) => {
    const isOutside = popRef.current === event.target;
    allowCloseOnClick.current = isOutside;
  };

  const handleMouseUp = (event: SyntheticEvent) => {
    if (!allowCloseOnClick.current) {
      return;
    }

    const isOutside = popRef.current === event.target;
    if (isOutside) {
      props.onClose();
      // Reset the popover so when it is re-opened the current state isn't loaded
      // This prevents erroneous values from a prior edit to persist
      setInputColor(pickerColor);
      setHelpText("");
      if (props.defaultColor) setDidRemoveColor(false);
    }
  };

  const pickerColorAsHexAlpha = colord(pickerColor).toHex();
  const inputColorAsString = typeof inputColor === "object" ? colord(inputColor).toHex() : inputColor;
  
  return (
    <>
      {props.isOpen &&
        <Popover
          ref={popRef}
          anchorEl={props.anchor}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          className="drag-cancel"
          sx={{
            "& .MuiPaper-root": {
              overflow: "visible", padding: "5px",
              width: "fit-content", maxWidth: "300px",
              ".react-colorful": {
                width: "100%",
                "& > div": {
                  marginBottom: "6px"
                }
              }
            }
          }}
          hideBackdrop={true}
          open={props.isOpen}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          {!props.disableAlpha &&
            <HexAlphaColorPicker color={pickerColorAsHexAlpha} onChange={handleInputColorChange} />
          }
          {props.disableAlpha &&
            <HexColorPicker color={pickerColorAsHexAlpha} onChange={handleInputColorChange} />
          }
          <Stack spacing={0.25} sx={{mt: 0.25}}>
            <Stack direction="row" spacing={0.5} sx={{ paddingLeft: 0.5, paddingRight: 0.5 }}>

              {/* Larger Color Swatch */}
              <Avatar
                sx={{
                  color: theme.colors.secondary.light,
                  background: pickerColor === '' ? theme.colors.secondary.lighter : colord(pickerColor).toHex(),
                  border: `1px solid ${theme.palette.grey[400]}`
                }}
                variant="rounded">
                {pickerColor === ''
                  ? <DoNotDisturb />
                  : <SquareRounded sx={{ color: "transparent" }} />}
              </Avatar>

              {/* Color Input Field */}
              {props.showInput &&
                <TextField
                  autoComplete="off"
                  fullWidth
                  size="small"
                  value={inputColorAsString}
                  error={helpText.length > 0}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => handleInputChange(event.target.value)} />}

              {/* Remove Color */}
              <IconButton
                size="small"
                sx={{
                  color: "red",
                  border: `1px solid ${theme.palette.grey[400]}`
                }}
                onClick={() => {
                  setPickerColor(props.defaultColor ? props.defaultColor : '');
                  setInputColor(props.defaultColor ? props.defaultColor : '');
                  props.onChange(props.defaultColor ? props.defaultColor : '');
                  setHelpText('');
                  setDebounceHelpText('');
                  if (props.defaultColor) setDidRemoveColor(true);
                }}
                >
                <DeleteForever />
              </IconButton>
            </Stack>
            <Box sx={{ textAlign: "center" }}><Typography sx={{ color: "red" }}>{helpText}</Typography></Box>
            {props.defaultColor && didRemoveColor && (pickerColor === props.defaultColor) && <Alert severity="warning">{i18n.t("color-selector.default-color-alert")}</Alert>}
            
            {/* Color Swatch Grid */}
            <Grid container spacing={0.5} sx={{margin: 0}}>
              {theme.colors.namedColors.colorPalletteArray.map((color, index) => (
                <Grid key={index}>
                  <ButtonBase
                    sx={{
                      cursor: "pointer",
                      width: "24px",
                      height: "24px",
                      backgroundColor: color,
                      borderRadius: theme.general.borderRadiusSm,
                      border: `1px solid ${darken(color, 0.1)}`,
                      display: "inline-block",
                      padding: 0,
                      minWidth: "24px",
                      "&:hover": {
                        background: lighten(color, 0.3)
                      }
                    }}
                    onClick={() => {
                      setPickerColor(color);
                      setInputColor(color);
                      props.onChange(color);
                      setHelpText('');
                      setDebounceHelpText('');
                    }}
                  >
                  </ButtonBase>
                </Grid>
                ))}
            </Grid>
          </Stack>
        </Popover>
      }
    </>
  );
}
